import { useState, useCallback, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';

import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';

import { useTheme } from '@mui/styles';
import type { DefaultTheme } from '@mui/styles';

import PageLayout from 'src/components/PageLayout';

import messages from './messages';
import SearchFilterBar, {
  type QuickFiltersType,
} from 'src/components/SearchFilterBar';
import DataTable from 'src/components/DataTable';
import ToggleSwitch from 'src/components/ToggleSwitch';

import { ReactComponent as CoffeeBreak } from 'src/images/CoffeeBreak.svg';
import StyledTab from 'src/components/StyledTab/StyledTab';
import StyledTabs from 'src/components/StyledTabs/StyledTabs';
import { API } from 'src/reactQuery/Api';
import {
  VerificationType,
  useDownloadVerificationFile,
  useGetCancellationProof,
  useGetVerificationFiles,
  useUpdateApproveVerificationFile,
  useUpdateRejectVerificationFile,
  useUpdateRetryVerificationFile,
} from 'src/pages/VerificationsPage/api';

import VerificationFileFields from './components/VerificationFileFields';
import VerificationCancellationProof from './components/VerificationCancellationProof';
import VerificationLanguageFields from './components/VerificationLanguageFields';
import ImageViewer from './components/ImageViewer';
import PdfViewer from './components/PdfViewer';
import VideoViewer from './components/VideoViewer';

enum VerificationStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  CHALLENGED = 'challenged',
}

enum CancellationProof {
  NO_PROOF = 'No Proof',
  WITH_PROOF = 'With Proof',
}

enum UserLanguageVideo {
  ONLY_WITH_VIDEO = 'Only with Video',
  ALL = 'All',
}

const navTabs: Array<{ value: VerificationType; label: string }> = [
  {
    value: 'matriculation_files',
    label: 'Matriculation',
  },
  {
    value: 'work_permissions',
    label: 'Work Permit',
  },
  {
    value: 'certificate_files',
    label: 'Health Certificate',
  },
  {
    value: 'pictures',
    label: 'Photo',
  },
  {
    value: 'user_languages',
    label: 'Language',
  },
  {
    value: 'cancellation_proofs',
    label: 'Cancellation Proof',
  },
];

const isPdfFile = (file: UCM.VerificationFilesType) => {
  if (!file) return false;
  const type = file?.contentType?.toLowerCase();
  const name = file?.fileName?.toLowerCase();
  return !!(type?.includes('pdf') || name?.endsWith('.pdf'));
};

const isImageFile = (file: UCM.VerificationFilesType) => {
  if (!file) return false;
  const type = file?.contentType?.toLowerCase();
  const name = file?.fileName?.toLowerCase();
  return !!(
    type?.includes('png') ||
    type?.includes('jpg') ||
    type?.includes('jpeg') ||
    name?.endsWith('.jpg') ||
    name?.endsWith('.png') ||
    name?.endsWith('.jpeg')
  );
};

const isVideoFile = (file: UCM.VerificationLanugagesType) => {
  if (!file) return false;
  const type = file?.contentType?.toLowerCase();
  const name = file?.fileName?.toLowerCase();
  return !!(
    type?.includes('mp4') ||
    type?.includes('ogg') ||
    type?.includes('webm') ||
    name?.endsWith('.mp4') ||
    name?.endsWith('.ogg') ||
    name?.endsWith('.webm')
  );
};

export default function VerificationsPage() {
  const theme = useTheme();
  const { formatMessage } = useIntl();

  const [activeTab, setActiveTab] = useState<VerificationType>(
    navTabs[0].value,
  );

  const [cancellationProofWithProof, setCancellationProofWithProof] =
    useState<CancellationProof>(CancellationProof.WITH_PROOF);

  const [userLanguageVideo, setUserLanguageVideo] = useState<UserLanguageVideo>(
    UserLanguageVideo.ONLY_WITH_VIDEO,
  );

  const [statusTab, setStatusTab] = useState<VerificationStatus>(
    VerificationStatus.PENDING,
  );

  const [filters, setFilters] = useState<Record<string, string>>({});
  const [params, setParams] = useState<Record<string, string | number>>({
    page: 1,
  });

  // eslint-disable-next-line
  const [selectedFile, setSelectedFile] = useState<any>(null);

  const filtersParams = useMemo(() => {
    if (
      activeTab === 'cancellation_proofs' &&
      cancellationProofWithProof === CancellationProof.WITH_PROOF
    ) {
      return {
        ...filters,
        status:
          statusTab === VerificationStatus.PENDING
            ? [statusTab, VerificationStatus.CHALLENGED]
            : [statusTab],
      };
    }

    if (activeTab === 'user_languages') {
      return {
        ...filters,
        only_with_video:
          userLanguageVideo === UserLanguageVideo.ONLY_WITH_VIDEO,
        status: statusTab,
      };
    }

    return {
      ...filters,
      status: statusTab,
    };
  }, [
    filters,
    statusTab,
    cancellationProofWithProof,
    userLanguageVideo,
    activeTab,
  ]);

  const verificationType = useMemo(() => {
    if (
      activeTab === 'cancellation_proofs' &&
      cancellationProofWithProof === CancellationProof.NO_PROOF
    ) {
      return 'without_proof';
    }

    return activeTab;
  }, [activeTab, cancellationProofWithProof]);

  const {
    data: verificationFiles,
    isFetching: isFetchingVerificationFiles,
    refetch: refetchVerificationFiles,
  } = useGetVerificationFiles({
    type: verificationType,
    filters: filtersParams,
    params,
  });

  const { data: cancellationProof } = useGetCancellationProof({
    uuid: selectedFile?.uuid ?? '',
    enabled: !!selectedFile?.proof && activeTab === 'cancellation_proofs',
  });

  const { mutate: approveFile, isPending: isApproving } =
    useUpdateApproveVerificationFile();

  const { mutate: rejectFile, isPending: isRejecting } =
    useUpdateRejectVerificationFile();

  const { mutate: retryFile, isPending: isRetrying } =
    useUpdateRetryVerificationFile();

  const searchFilters = useMemo(() => {
    return [
      {
        id: 'user_id',
        type: 'text' as QuickFiltersType['type'],
        placeholder: formatMessage(messages.searchUidPlaceholder),
      },
    ];
  }, []);

  const handleActiveTabChange = useCallback((tab: VerificationType) => {
    setActiveTab(tab);
    setParams({ page: 1 });
    setSelectedFile(null);
  }, []);

  const handlePagination = useCallback((_: unknown, page: number) => {
    setParams({ page: page + 1 });
  }, []);

  // eslint-disable-next-line
  const handleSearch = useCallback((filters: Record<string, any>) => {
    setParams({ page: 1 });
    setFilters(filters);
  }, []);

  const handleRefreshList = useCallback(() => {
    setParams({ page: 1 });
    void refetchVerificationFiles();
  }, []);

  const columns: Array<{
    id: string;
    label: string;
  }> = useMemo(
    () => [
      {
        id: 'uid',
        label: formatMessage(messages.UIDLabel),
        render: (_: unknown, item: UCM.VerificationFilesType) => (
          <Typography variant="inherit">{item.user?.id ?? '-'}</Typography>
        ),
        align: 'center',
      },
      {
        id: 'name',

        label: formatMessage(messages.studentNameLabel),
        render: (_: unknown, item: UCM.VerificationFilesType) => (
          <Typography variant="inherit">
            {item.user?.firstName} {item.user?.lastName}
          </Typography>
        ),
      },
      ...(activeTab === 'user_languages'
        ? [
            {
              id: 'language',
              label: formatMessage(messages.languageLabel),
              render: (_: unknown, item: UCM.VerificationLanugagesType) => (
                <Typography variant="inherit">{item.language.name}</Typography>
              ),
            },
          ]
        : []),
      ...(activeTab === 'cancellation_proofs' &&
      cancellationProofWithProof === CancellationProof.WITH_PROOF
        ? [
            {
              id: 'createdAt',
              label: formatMessage(messages.createdAtLabel),
              render: (createdAt: string) => (
                <Typography variant="inherit">{createdAt}</Typography>
              ),
            },
          ]
        : []),
      {
        id: 'uploadedAt',
        align: 'center',
        label:
          activeTab === 'cancellation_proofs' &&
          cancellationProofWithProof === CancellationProof.NO_PROOF
            ? formatMessage(messages.createdAtLabel)
            : formatMessage(messages.uploadedAtLabel),
        render: (
          _: string,
          item:
            | UCM.VerificationFilesType
            | UCM.VerificationCancellationProofsType,
        ) => {
          const createdAt =
            'cancellationNotice' in item
              ? item.cancellationNotice?.createdAt
              : item.createdAt;
          return <Typography variant="inherit">{createdAt ?? '-'}</Typography>;
        },
      },
    ],
    [activeTab, cancellationProofWithProof],
  );

  const tabStatuses = useMemo(() => {
    return [
      {
        value: VerificationStatus.PENDING,
        label: formatMessage(messages.pendingLabel),
        badge: 0,
      },
      {
        value: VerificationStatus.APPROVED,
        label: formatMessage(messages.approvedLabel),
        badge: 0,
      },
      {
        value: VerificationStatus.REJECTED,
        label: formatMessage(messages.rejectedLabel),
        badge: 0,
      },
    ];
  }, []);

  const changeTab = useCallback(
    (_: React.SyntheticEvent, newValue: VerificationStatus) => {
      setStatusTab(newValue);
    },
    [],
  );

  // eslint-disable-next-line
  const handleSelectRow = useCallback((file: any) => {
    setSelectedFile(file);
  }, []);

  const toolbarSx = useRef((theme: DefaultTheme) => ({
    backgroundColor: theme.palette.grey[100],
  })).current;

  const handleApprove = useCallback(
    (params: Record<string, string | object>) => {
      if (!selectedFile) return;
      let type: string = verificationType;
      if (verificationType === 'pictures') {
        type = 'photos';
      }
      if (verificationType === 'user_languages') {
        const file = selectedFile as UCM.VerificationLanugagesType;
        type = `languages/${file?.language.code}`;
      }
      approveFile(
        {
          type,
          fileUuid: selectedFile?.uuid,
          userUuid: selectedFile?.user.uuid,
          params,
        },
        {
          onSuccess: () => {
            setSelectedFile(null);
          },
        },
      );
    },
    [selectedFile, verificationType],
  );

  const handleReject = useCallback(
    (params: Record<string, string | object>) => {
      if (!selectedFile) return;
      let type: string = verificationType;
      if (verificationType === 'pictures') {
        type = 'photos';
      }
      if (verificationType === 'user_languages') {
        const file = selectedFile as UCM.VerificationLanugagesType;
        type = `languages/${file.language?.code}`;
      }
      rejectFile(
        {
          type,
          fileUuid: selectedFile?.uuid,
          userUuid: selectedFile?.user.uuid,
          params,
        },
        {
          onSuccess: () => {
            setSelectedFile(null);
          },
        },
      );
    },
    [selectedFile, verificationType],
  );

  const handleRetry = useCallback(
    (params: Record<string, string>) => {
      if (!selectedFile) return;
      retryFile(
        {
          type: verificationType,
          fileUuid: selectedFile?.uuid,
          params,
        },
        {
          onSuccess: () => {
            setSelectedFile(null);
          },
        },
      );
    },
    [selectedFile, verificationType],
  );
  const CurrentTypeComponent = useMemo(() => {
    if (activeTab.match(/cancellation_proofs|without_proof/)) {
      return VerificationCancellationProof;
    }
    if (activeTab === 'user_languages') {
      return VerificationLanguageFields;
    }
    return VerificationFileFields;
  }, [activeTab]);

  const ViewerComponent = useMemo(() => {
    let component = null;

    const file =
      activeTab === 'cancellation_proofs' &&
      cancellationProofWithProof === CancellationProof.WITH_PROOF &&
      selectedFile
        ? cancellationProof
        : selectedFile;

    if (!file) return {};
    if (isImageFile(file as UCM.VerificationFilesType)) {
      component = ImageViewer;
    }
    if (isPdfFile(file as UCM.VerificationFilesType)) {
      component = PdfViewer;
    }
    if (isVideoFile(file as UCM.VerificationLanugagesType)) {
      component = VideoViewer;
    }

    // Maps the active tab to the matching BE download endpoint. Videos
    // (user_languages) have no download endpoint, so no path is set.
    let downloadPath;
    if (activeTab === 'cancellation_proofs') {
      downloadPath = API.GET.downloadCancellationProof(file.uuid);
    } else if (activeTab !== 'user_languages') {
      downloadPath = API.GET.downloadVerificationAsset(activeTab, file.uuid);
    }

    return {
      component,
      url: file.url,
      contentType: file.contentType,
      fileName: file.fileName,
      downloadPath,
    };
  }, [selectedFile, cancellationProof, activeTab]);

  const { mutate: downloadFile } = useDownloadVerificationFile();

  const handleDownloadFile = useCallback(() => {
    if (!ViewerComponent.downloadPath) return;
    downloadFile({
      path: ViewerComponent.downloadPath,
      fileName: ViewerComponent.fileName,
      contentType: ViewerComponent.contentType,
    });
  }, [downloadFile, ViewerComponent]);

  const handleChangeProof = useCallback((value: CancellationProof) => {
    setSelectedFile(null);
    setCancellationProofWithProof(value);
  }, []);

  const handleChangeVideo = useCallback((value: UserLanguageVideo) => {
    setSelectedFile(null);
    setUserLanguageVideo(value);
  }, []);

  const Toolbar = useMemo(() => {
    return (
      <Grid container size={12} spacing={1} justifyContent="space-between">
        <StyledTabs value={statusTab} onChange={changeTab}>
          {tabStatuses.map((tabItem, index) => (
            <StyledTab
              key={index}
              label={tabItem.label}
              badge={tabItem?.badge}
              value={tabItem.value}
            />
          ))}
        </StyledTabs>
        {activeTab === 'user_languages' && (
          <ToggleSwitch
            selected={userLanguageVideo}
            onChange={handleChangeVideo}
            options={[
              formatMessage(messages.onlyWithVideoSwitchLabel),
              formatMessage(messages.allVideoSwitchLabel),
            ]}
            fontSize="10px"
          />
        )}
        {activeTab === 'cancellation_proofs' && (
          <ToggleSwitch
            selected={cancellationProofWithProof}
            onChange={handleChangeProof}
            options={[
              formatMessage(messages.noProofSwitchLabel),
              formatMessage(messages.withProofSwitchLabel),
            ]}
            fontSize="10px"
          />
        )}
      </Grid>
    );
  }, [
    activeTab,
    statusTab,
    cancellationProofWithProof,
    userLanguageVideo,
    changeTab,
    handleChangeVideo,
    handleChangeProof,
  ]);

  const EmptyComponent = useMemo(() => {
    return (
      <Stack alignItems="center">
        <Stack width={270} alignItems="center">
          <CoffeeBreak width={270} />
          <Typography fontSize={20} mb={2}>
            {formatMessage(messages.noDataMessage)}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleRefreshList}
          >
            {formatMessage(messages.refreshListButton)}
          </Button>
        </Stack>
      </Stack>
    );
  }, [handleRefreshList]);

  return (
    <PageLayout
      iconName="verification"
      title={formatMessage(messages.title)}
      tabs={navTabs}
      activeTab={activeTab}
      onActiveTabChange={handleActiveTabChange}
    >
      <Grid container spacing={1.5} alignItems="center">
        <Grid size={{ xs: 12, sm: 2.5 }}>
          <SearchFilterBar
            fields={searchFilters}
            onSearch={handleSearch}
            variant="noBorder"
          />
        </Grid>

        <Grid size={12} container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Stack
              borderRadius={1.5}
              minHeight="calc(100vh - 170px)"
              maxHeight="calc(100vh - 170px)"
              border="1px solid"
              borderColor={theme.palette.grey[500]}
              overflow="hidden"
              justifyContent="center"
              alignItems="center"
              position="relative"
            >
              {ViewerComponent.component && (
                <ViewerComponent.component
                  key={selectedFile?.uuid}
                  url={ViewerComponent.url}
                  contentType={ViewerComponent.contentType}
                  onDownload={
                    ViewerComponent.downloadPath
                      ? handleDownloadFile
                      : undefined
                  }
                />
              )}
            </Stack>
          </Grid>
          <Grid
            size={{ xs: 12, md: 6 }}
            container
            spacing={2}
            alignContent="flex-start"
          >
            <Grid size={12}>
              <CurrentTypeComponent
                file={selectedFile}
                tab={activeTab}
                handleApprove={handleApprove}
                handleReject={handleReject}
                handleRetry={handleRetry}
                loading={isApproving || isRejecting || isRetrying}
                cancellationProof={cancellationProof}
              />
            </Grid>
            <DataTable
              rows={verificationFiles?.data ?? []}
              columns={columns}
              loading={isFetchingVerificationFiles}
              pagination={verificationFiles?.meta}
              onPaginate={handlePagination}
              activeRow={selectedFile}
              pressRow={(row) => handleSelectRow(row)}
              checkActiveKey="uuid"
              toolbar={Toolbar}
              toolbarSx={toolbarSx}
              customEmptyComponent={EmptyComponent}
            />
          </Grid>
        </Grid>
      </Grid>
    </PageLayout>
  );
}
